// src/pages/EditCourse.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { getCourse, updateCourse } from "../firebase/database";

export default function EditCourse() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: "",
    totalLessons: "",
    totalDurationHours: "",
    classHours: "",
    practiceHours: "",
    fees: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadCourse();
  }, [courseId]);

  const loadCourse = async () => {
    try {
      const course = await getCourse(courseId);
      if (course) {
        setFormData({
          name: course.name || "",
          totalLessons: course.totalLessons?.toString() || "",
          totalDurationHours: course.totalDurationHours?.toString() || "",
          classHours: course.classHours?.toString() || "",
          practiceHours: course.practiceHours?.toString() || "",
          fees: course.fees?.toString() || "",
        });
      }
    } catch (err) {
      console.error("Error loading course:", err);
      setError("Failed to load course");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name.trim()) {
      setError("Course name is required");
      return;
    }

    setSaving(true);
    try {
      await updateCourse(courseId, formData);
      navigate("/admin/courses");
    } catch (err) {
      console.error("Error updating course:", err);
      setError("Failed to update course");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
        <div style={{ color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", padding: "24px" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <Link 
            to="/admin/courses" 
            style={{ color: "#6B7280", textDecoration: "none", display: "flex", alignItems: "center" }}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
            Edit Course
          </h1>
        </div>
        <p style={{ color: "#6B7280", margin: "4px 0 0 36px", fontFamily: "DM Sans, sans-serif" }}>
          Update course details
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} style={{ 
        background: "white", 
        borderRadius: 12, 
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)", 
        padding: 24 
      }}>
        {error && (
          <div style={{ 
            background: "#FEE2E2", 
            color: "#DC2626", 
            padding: "12px 16px", 
            borderRadius: 8, 
            marginBottom: 20,
            fontFamily: "DM Sans, sans-serif"
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Course Name */}
          <div>
            <label style={{ 
              display: "block", 
              fontSize: 14, 
              fontWeight: 600, 
              color: "#374151", 
              marginBottom: 8,
              fontFamily: "DM Sans, sans-serif"
            }}>
              Course Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Python Programming"
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #D1D5DB",
                borderRadius: 8,
                fontSize: 16,
                fontFamily: "DM Sans, sans-serif",
                boxSizing: "border-box",
                outline: "none",
              }}
            />
          </div>

          {/* Number of Lessons */}
          <div>
            <label style={{ 
              display: "block", 
              fontSize: 14, 
              fontWeight: 600, 
              color: "#374151", 
              marginBottom: 8,
              fontFamily: "DM Sans, sans-serif"
            }}>
              Number of Lessons
            </label>
            <input
              type="number"
              name="totalLessons"
              value={formData.totalLessons}
              onChange={handleChange}
              placeholder="e.g., 30"
              min="0"
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #D1D5DB",
                borderRadius: 8,
                fontSize: 16,
                fontFamily: "DM Sans, sans-serif",
                boxSizing: "border-box",
                outline: "none",
              }}
            />
          </div>

          {/* Duration & Hours Row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            <div>
              <label style={{ 
                display: "block", 
                fontSize: 14, 
                fontWeight: 600, 
                color: "#374151", 
                marginBottom: 8,
                fontFamily: "DM Sans, sans-serif"
              }}>
                Total Duration (hrs)
              </label>
              <input
                type="number"
                name="totalDurationHours"
                value={formData.totalDurationHours}
                onChange={handleChange}
                placeholder="e.g., 60"
                min="0"
                step="0.5"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 8,
                  fontSize: 16,
                  fontFamily: "DM Sans, sans-serif",
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 14, 
                fontWeight: 600, 
                color: "#374151", 
                marginBottom: 8,
                fontFamily: "DM Sans, sans-serif"
              }}>
                Class Hours
              </label>
              <input
                type="number"
                name="classHours"
                value={formData.classHours}
                onChange={handleChange}
                placeholder="e.g., 30"
                min="0"
                step="0.5"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 8,
                  fontSize: 16,
                  fontFamily: "DM Sans, sans-serif",
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 14, 
                fontWeight: 600, 
                color: "#374151", 
                marginBottom: 8,
                fontFamily: "DM Sans, sans-serif"
              }}>
                Practice Hours
              </label>
              <input
                type="number"
                name="practiceHours"
                value={formData.practiceHours}
                onChange={handleChange}
                placeholder="e.g., 30"
                min="0"
                step="0.5"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 8,
                  fontSize: 16,
                  fontFamily: "DM Sans, sans-serif",
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
            </div>
          </div>

          {/* Fees */}
          <div>
            <label style={{ 
              display: "block", 
              fontSize: 14, 
              fontWeight: 600, 
              color: "#374151", 
              marginBottom: 8,
              fontFamily: "DM Sans, sans-serif"
            }}>
              Fees (₹)
            </label>
            <input
              type="number"
              name="fees"
              value={formData.fees}
              onChange={handleChange}
              placeholder="e.g., 5000"
              min="0"
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #D1D5DB",
                borderRadius: 8,
                fontSize: 16,
                fontFamily: "DM Sans, sans-serif",
                boxSizing: "border-box",
                outline: "none",
              }}
            />
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              flex: 1,
              padding: "14px 24px",
              background: saving ? "#9CA3AF" : "#3B82F6",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              fontFamily: "DM Sans, sans-serif",
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
          <Link
            to="/admin/courses"
            style={{
              padding: "14px 24px",
              background: "white",
              color: "#6B7280",
              border: "1px solid #D1D5DB",
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              fontFamily: "DM Sans, sans-serif",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}